import { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { FormSelect, FormTextArea } from '../forms';

const MSE_SECTIONS = {
  appearance: {
    label: 'Appearance',
    options: {
      dressing: ['Normal', 'Inappropriate', 'Other'],
      grooming: ['Normal', 'Inappropriate'],
      age: ['Looking their age', 'Looking older', 'Looking younger']
    }
  },
  attitude: {
    label: 'Attitude',
    options: ['Positive', 'Negative', 'Cooperative', 'Uncooperative', 'Hostile', 'Other']
  },
  behavior: {
    label: 'Behavior',
    options: {
      ritualized: ['None Present', 'Present'],
      unwantedBehavior: ['None Present', 'Present'],
      nonverbalBehavior: ['Normal', 'Inappropriate'],
      activityLevel: ['Normal', 'Hyperactive', 'Hypoactive']
    }
  },
  speech: {
    label: 'Speech',
    options: {
      style: ['Normal', 'Inappropriate'],
      volume: ['Normal', 'Loud', 'Low'],
      speed: ['Normal', 'Fast', 'Slow'],
      tone: ['Normal', 'High', 'Low']
    }
  },
  mood: {
    label: 'Mood',
    options: ['Euthymic', 'Hyperthymic', 'Dysthymic', 'Irritable', 'Anxious']
  },
  affect: {
    label: 'Affect/Display of Emotions',
    options: ['Appropriate', 'Inappropriate', 'Flat', 'Blunted', 'Labile']
  },
  orientation: {
    label: 'Orientation',
    options: ['Fully Oriented', 'Partially Oriented', 'Disoriented']
  },
  cognition: {
    label: 'Cognitive Functioning',
    options: ['Normal/Good', 'Mildly Impaired', 'Moderately Impaired', 'Severely Impaired']
  },
  thoughtContent: {
    label: 'Thought Content',
    options: {
      contentAndOrganization: ['Normal', 'Poor'],
      negativeThinking: ['Yes', 'No'],
      obsessions: ['Yes', 'No'],
      phobias: ['Yes', 'No'],
      suicidalIdeation: ['None', 'Passive', 'Active'],
      homicidalIdeation: ['None', 'Passive', 'Active'],
      homicidalPlan: ['Yes', 'No'],
      homicidalIntent: ['Yes', 'No'],
      homicidalMeans: ['Yes', 'No']
    }
  },
  perceptualDisturbances: {
    label: 'Perceptual Disturbances',
    options: ['None Present', 'Hallucinations', 'Illusions']
  },
  memory: {
    label: 'Memory',
    options: {
      shortTerm: ['Intact', 'Impaired'],
      longTerm: ['Intact', 'Impaired']
    }
  },
  concentration: {
    label: 'Concentration',
    options: ['Normal', 'Easily Distracted', 'Poor']
  },
  insight: {
    label: 'Insight',
    options: ['Good', 'Fair', 'Poor']
  },
  judgment: {
    label: 'Judgment',
    options: ['Good', 'Fair', 'Poor']
  }
};

export const MentalStatusExam = ({ sessionId, initialData, onSubmit }) => {
  const { put } = useApi();
  const [formData, setFormData] = useState(initialData || {});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (section, subsection, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: subsection
        ? { ...(prev[section] || {}), [subsection]: value }
        : value
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors(null);

    try {
      await onSubmit(formData);
    } catch (err) {

      setErrors(err.message || 'Failed to submit consent form.');
    } finally {
      setLoading(false);
    }
  };


  const renderSection = (section, config) => {
    // Special handling for thought content section
    if (section === 'thoughtContent') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(config.options).map(([subsection, options]) => {
            // Only show homicidal plan/intent/means if homicidalIdeation is 'active'
            if (['homicidalPlan', 'homicidalIntent', 'homicidalMeans'].includes(subsection) &&
              formData[section]?.homicidalIdeation?.toLowerCase() !== 'active') {
              return null;
            }

            return (
              <FormSelect
                key={subsection}
                label={subsection
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, str => str.toUpperCase())}
                value={formData[section]?.[subsection] || ''}
                onChange={(e) => handleChange(section, subsection, e.target.value)}
                options={options.map(opt => ({ value: opt.toLowerCase(), label: opt }))}
                error={errors[section]?.[subsection]}
              />
            );
          })}
        </div>
      );
    }

    // Handle regular sections
    return typeof config.options === 'object' && !Array.isArray(config.options) ? (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(config.options).map(([subsection, options]) => (
          <FormSelect
            key={subsection}
            label={subsection.charAt(0).toUpperCase() + subsection.slice(1)}
            value={formData[section]?.[subsection] || ''}
            onChange={(e) => handleChange(section, subsection, e.target.value)}
            options={options.map(opt => ({ value: opt.toLowerCase(), label: opt }))}
            error={errors[section]?.[subsection]}
          />
        ))}
      </div>
    ) : (
      <FormSelect
        value={formData[section] || ''}
        onChange={(e) => handleChange(section, null, e.target.value)}
        options={config.options.map(opt => ({ value: opt.toLowerCase(), label: opt }))}
        error={errors[section]}
      />
    );
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-medium text-gray-900">Mental Status Examination</h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-6">
          {Object.entries(MSE_SECTIONS).map(([section, config]) => (
            <div key={section} className="border-b pb-6">
              <h3 className="text-md font-medium text-gray-900 mb-4">{config.label}</h3>
              {renderSection(section, config)}
              <FormTextArea
                className="mt-4"
                placeholder={`Additional notes for ${config.label.toLowerCase()}`}
                value={formData[`${section}Notes`] || ''}
                onChange={(e) => handleChange(`${section}Notes`, null, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-6">
          <button
            type="submit"
            className="px-6 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600"
          >
            Save Mental Status Exam
          </button>
        </div>
      </form>
    </div>
  );
};
